import { useEffect, useState } from "react";
import { Autocomplete, Chip, TextField, Tooltip } from "@mui/material";

export default function AutocompleteChipsView(props) {
  const { objList, lang, onChange, label, required, helperText, placeholder } = props;

  // Local reference to your domain object
  const [valueObjList, setValueObjList] = useState(objList);

  // Chips shown by Autocomplete
  const [chips, setChips] = useState([]);

  // Raw text inside the textbox
  const [inputValue, setInputValue] = useState("");

  // Sync when parent replaces objList
  useEffect(() => {
    setValueObjList(objList);
    const list = objList?.getList?.() ?? [];
    const nextChips = list
      .filter((it) => it.containsLang(lang))
      .map((it) => it.getValue(lang));
    setChips(nextChips);
  }, [objList, lang]);

  const addChip = (val) => {
    const v = (val ?? "").trim();
    if (!v || chips.includes(v)) return;

    // Clone/produce new domain instance (adjust to your class API)
    const cloned = valueObjList?.clone
      ? valueObjList.clone()
      : valueObjList.copy?.()
        ? valueObjList.copy()
        : valueObjList; // fallback; ideally implement clone/copy

    cloned.addLanguageValue(lang, v);

    // Update local states with new references
    setValueObjList(cloned);
    setChips((prev) => [...prev, v]);
    onChange?.(cloned);

    // Clear text so it doesn't appear next to chips
    setInputValue("");
  };

  const removeChipAt = (index) => {
    const toRemove = chips[index];
    if (toRemove == null) return;

    const cloned = valueObjList?.clone
      ? valueObjList.clone()
      : valueObjList.copy?.()
        ? valueObjList.copy()
        : valueObjList;

    // Your domain API removes by index for lang
    cloned.removeItemOfLang(index, lang);

    setValueObjList(cloned);
    setChips((prev) => prev.filter((_, i) => i !== index));
    onChange?.(cloned);
  };

  // Fired when user confirms a chip (press Enter or picks an option)
  const handleChange = (_e, newValues) => {
    // Compute adds/removes compared to current chips
    const toAdd = newValues.filter((v) => !chips.includes(v));
    const toRemove = chips.filter((v) => !newValues.includes(v));

    toAdd.forEach(addChip);
    // Remove by value – map to current indexes each time
    toRemove.forEach((v) => {
      const idx = chips.indexOf(v);
      if (idx >= 0) removeChipAt(idx);
    });
  };

  const handleInputChange = (_e, next) => {
    setInputValue(next ?? "");
  };

  const handleBlur = (e) => {
    const typed = e.target.value?.trim();
    if (typed) addChip(typed);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      clearIcon={false}
      options={[]}
      value={chips}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={`${option}-${index}`}
            label={option}
            {...getTagProps({ index })}
            onDelete={() => removeChipAt(index)}
          />
        ))
      }
      renderInput={(params) => (
        <Tooltip followCursor placement="top-start" title="Press Enter after typing">
          <TextField
            {...params}
            variant="filled"
            label={label}
            required={required}
            helperText={helperText}
            placeholder={placeholder}
            onBlur={handleBlur}
          />
        </Tooltip>
      )}
    />
  );
}
